import { Skeleton } from '@acctglobal/skeleton'
import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import lazyRetry from 'src/utils/lazyRetry'
import type {
  CmsHomeConnection,
  CmsInstitutionalPageConnection,
  CmsInstitutionalPageEdge,
  ProductSummary_ProductFragment,
  SiteSiteMetadata,
} from '@generated/graphql'
import type { TelemarketingStatus } from 'src/sdk/telemarketing/getTelemarketingStatus'
import getTelemarketingStatus from 'src/sdk/telemarketing/getTelemarketingStatus'

import type { CmsSection } from '../Header/Header'
import useWindowDimensions from 'src/hooks/useWindowDimensions'

const TelemarketingBar = lazy(() =>
  lazyRetry(
    () => import('../TelemarketingBar/TelemarketingBar'),
    'TelemarketingBar'
  )
)

const Header = lazy(() => lazyRetry(() => import('../Header'), 'Header'))

const HeaderLife = lazy(() =>
  lazyRetry(() => import('../HeaderLife'), 'HeaderLife')
)

export type LocationCms = {
  product: ProductSummary_ProductFragment
  cmsGlobalComponents?: Record<string, CmsSection[]>
  allCmsInstitutionalPage?: CmsInstitutionalPageConnection
  allCmsHome?: CmsHomeConnection
  site?: { SiteMetadata: SiteSiteMetadata }
}

interface HeaderContainerProps {
  isPDP: boolean
  location: LocationCms | null
  isLife: boolean | null
  setIsLife: React.Dispatch<React.SetStateAction<boolean | null>>
  slug: string | undefined
  edges?: CmsInstitutionalPageEdge[]
  pathname: string
  brandName: string | undefined
  isStorageAvailable: () => boolean
}

const HeaderContainer = ({
  isPDP,
  location,
  isLife,
  setIsLife,
  slug,
  edges,
  pathname,
  brandName,
  isStorageAvailable,
}: HeaderContainerProps) => {
  const [isTelemarketing, setIsTelemarketing] = useState<string | null>(null)
  const [lastScroll, setLastScroll] = useState<number>(0)
  const [stickyHeaderClass, setStickyHeaderClass] = useState<string>('scroll-up')
  const [renderHeader, setRenderHeader] = useState(false)
  const [loadLife, setLoadLife] = useState(true)
  const [hideTelemarketingBar, setHideTelemarketingBar] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [telemarketingStatus, setTelemarketingStatus] = useState<
    TelemarketingStatus | undefined
  >()
  const [searchOpen, setSearchOpen] = useState<boolean>(true)
  const { width } = useWindowDimensions()
  const isMobile = width < 1024

  const headerRef = useRef<HTMLDivElement>(null)

  const filterInstitutionalPage = edges?.filter((edge) => {
    const slugPage = edge?.node?.config?.slugAndFilterConfig?.slug
    const validatedPathname =
      pathname?.slice(-1) === '/' ? pathname?.slice(0, -1) : pathname

    return slugPage === validatedPathname
  })

  const headerTypeMap = filterInstitutionalPage?.map((element) => {
    return element.node?.config?.slugAndFilterConfig?.headerType
  })

  const headerType =
    (headerTypeMap?.length || 0) > 0
      ? typeof headerTypeMap === 'object' && headerTypeMap[0]
      : 'Padrão'

  const handleHideOrShowHeaderAfterScroll = useCallback(() => {
    const currentScrollTop =
    window?.scrollY || document?.documentElement.scrollTop
    
    if (currentScrollTop < 0) {
      return
    }

    const searchResultIsOpen = !!document.getElementsByClassName("searchSuggestions__title").length

    if(searchResultIsOpen) {
      if(stickyHeaderClass === 'scroll-up' && headerRef.current){

        headerRef.current.style.marginTop = isMobile ? "-76px" : "-152px"
      }
      return
    }
    if(headerRef.current && ["-76px", "-152px"].includes(headerRef.current.style.marginTop)){
      headerRef.current.style.marginTop = "0px"
    }

    if (currentScrollTop < 100 && stickyHeaderClass === 'scroll-down') {
      setStickyHeaderClass('scroll-up')
      document.activeElement && (document.activeElement as any).blur()
      return
    }

    if(document.activeElement?.tagName === "INPUT") {
      return
    } 
    
    if (currentScrollTop > lastScroll && stickyHeaderClass !== 'scroll-down') {
      setStickyHeaderClass('scroll-down')
    } else if (
      currentScrollTop < lastScroll &&
      stickyHeaderClass === 'scroll-down'
    ) {
      setStickyHeaderClass('scroll-up')
    }

    setLastScroll(currentScrollTop)
  }, [lastScroll, stickyHeaderClass])

  useEffect(() => {
    window?.['addEventListener'](
      'scroll',
      handleHideOrShowHeaderAfterScroll
    )

    return () =>
      window?.removeEventListener('scroll', handleHideOrShowHeaderAfterScroll)
  }, [handleHideOrShowHeaderAfterScroll, isPDP])

  useEffect(() => {
    setRenderHeader(true)
  }, [])

  useEffect(() => {
    const isThemeLife = isStorageAvailable()
      ? window.localStorage.getItem('isThemeLife')
      : 'false'

    const shouldSetLife =
      headerType === 'Life' ||
      (pathname.includes('/s/') && isThemeLife === 'true') ||
      pathname.includes('/life') ||
      brandName === 'Life by Vivara' ||
      slug === 'life'

    setIsLife(shouldSetLife)

    if (isStorageAvailable()) {
      window.localStorage.setItem(
        'isThemeLife',
        shouldSetLife ? 'true' : 'false'
      )
    }

    setLoadLife(false)
  }, [slug, pathname, headerType, isStorageAvailable, brandName, setIsLife])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    let cachedTelemarketingStatus =
      isStorageAvailable() && localStorage.getItem('telemarketing')

    if (cachedTelemarketingStatus === 'undefined') {
      cachedTelemarketingStatus = '{}'
    }

    const { sessionToken } =
      typeof cachedTelemarketingStatus === 'string'
        ? JSON.parse(cachedTelemarketingStatus)
        : {
            sessionToken: null,
          }

    getTelemarketingStatus(sessionToken)
      .then((res) => {
        setTelemarketingStatus(res)
        setIsTelemarketing(res?.userEmail)
        setHideTelemarketingBar(false)
        setIsLoading(false)
      })
      .catch(() => {
        setHideTelemarketingBar(true)
        setIsLoading(false)
      })
  }, [hideTelemarketingBar, setIsTelemarketing])

  useEffect(() => {
    const currentScrollTop =
    window?.scrollY || document?.documentElement.scrollTop
    if (currentScrollTop < 100 && !searchOpen) {
      setStickyHeaderClass('scroll-up')
      document.activeElement && (document.activeElement as any).blur()

      if(headerRef.current && ["-76px", "-152px"].includes(headerRef.current.style.marginTop)){
        headerRef.current.style.marginTop = "0px"
      }
    }
  }, [searchOpen])

  return (
    <div
      className={`layout-header-wrapper ${stickyHeaderClass} ${
        isTelemarketing ? 'telemarketing' : ''
      }`}
      ref={headerRef}
    >
      {!hideTelemarketingBar && !isLoading && (
        <TelemarketingBar
          setTelemarketingStatus={setTelemarketingStatus}
          telemarketingStatus={telemarketingStatus}
          hideTelemarketingBar={hideTelemarketingBar}
        />
      )}
      {loadLife || !renderHeader ? (
        <Skeleton height="160px" width="100vw" />
      ) : isLife ? (
        <Suspense fallback={null}>
          <HeaderLife
            isPDP={isPDP}
            stickyHeaderClass={stickyHeaderClass}
            location={location}
            setSearchOpen={setSearchOpen}
          />
        </Suspense>
      ) : (
        <Suspense fallback={null}>
          <Header
            isPDP={isPDP}
            stickyHeaderClass={stickyHeaderClass}
            location={location}
            setSearchOpen={setSearchOpen}
          />
        </Suspense>
      )}
    </div>
  )
}

export default HeaderContainer
